// Redirection Manager tab — view / edit / delete / add the 301-302 redirects stored on the
// connected WordPress site by the LSG plugin. Auto-created redirects (from Sitemap parent-page
// changes) appear here alongside any you add manually. Reads/writes via the plugin's
// /seo-audit/v1/redirects REST routes (through the WP client).

import { useState, useEffect, useCallback, useRef, useLayoutEffect, type CSSProperties } from "react";
import { showConfirm } from "../lib/dialog";
import { recordHistory } from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import type { WPClient, LsgRedirect } from "../lib/wpClient";

interface Props {
  getClient: () => WPClient | null;
  connected: boolean;
  onToast?: (t: { type: "success" | "error" | "info"; message: string }) => void; // window toast
  initialRows?: LsgRedirect[];        // cached list (from App) → show instantly on open, no reload flash
  onRedirectsChanged?: () => void;    // notify App after a create/update/delete so its cache stays fresh
}

const fieldInput: CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 6,
  border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", fontSize: 12,
  outline: "none", fontFamily: "'Inter','Mulish',sans-serif",
};
const actBtn = (bg: string): CSSProperties => ({ padding: "6px 12px", background: bg, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" });

// "Created By" pills are styled in app.css (.rm-pill / .rm-pill-lsg / .rm-pill-manual) so the Manual
// pill can be theme-aware and match the cover business name color in both light and dark.

// Redirect timestamps arrive as MySQL datetimes ("YYYY-MM-DD HH:MM:SS"). Format the DATE for the
// list; fall back to the raw date part if it can't be parsed, or an em dash when absent.
function fmtRedirectDate(s: string): string {
  if (!s) return "—";
  const d = new Date(s.replace(" ", "T"));
  if (isNaN(d.getTime())) return s.slice(0, 10) || "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Created-By pill label + style class from the stored createdBy value. A "-edited" suffix (set when a
// user edits an imported/LSG redirect in this tab) appends " - Edited" to the label, same pill color.
function createdByPill(cb: string): { label: string; cls: string } {
  const edited = cb.endsWith("-edited");
  const base = edited ? cb.slice(0, -"-edited".length) : cb;
  const label = base === "manual" ? "Manual" : base === "import" ? "Import" : "LSG";
  const cls = base === "manual" ? "rm-pill-manual" : base === "import" ? "rm-pill-import" : "rm-pill-lsg";
  return { label: edited ? `${label} - Edited` : label, cls };
}

// Normalize a source path for duplicate matching against existing redirects (lowercase, no trailing
// slash, path-only). Approximates the plugin's own source normalization.
function normSource(s: string): string {
  const raw = (s || "").trim();
  let path = raw, query = "";
  try {
    if (/^https?:\/\//i.test(raw)) { const u = new URL(raw); path = u.pathname; query = u.search.replace(/^\?/, ""); }
    else { const hashless = raw.replace(/#.*$/, ""); const qi = hashless.indexOf("?"); if (qi >= 0) { path = hashless.slice(0, qi); query = hashless.slice(qi + 1); } else path = hashless; }
  } catch { path = raw.replace(/[#?].*$/, ""); }
  path = path.replace(/\/+$/, "").toLowerCase();
  if (!path) path = "/";
  if (!path.startsWith("/")) path = `/${path}`;
  // Preserve a query string (sorted) so /?page_id=16180 is distinct from the home page — mirrors the plugin.
  if (query) {
    try { const p = new URLSearchParams(query); p.sort(); const q = p.toString(); return q ? `${path}?${q}` : path; } catch { return `${path}?${query}`; }
  }
  return path;
}

// Parse CSV text into rows of fields (RFC-4180: quoted fields, "" escapes, \r\n or \n line breaks).
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "", row: string[] = [], inQ = false;
  const t = text.replace(/^﻿/, ""); // strip BOM
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inQ) {
      if (c === '"') { if (t[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c !== "\r") field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

export function RedirectionManager({ getClient, connected, onToast, initialRows, onRedirectsChanged }: Props) {
  // Seed from App's cached redirects so the tab renders instantly on open; a background load still
  // refreshes it (no "Loading…" flash when we already have cached rows).
  const [rows, setRows] = useState<LsgRedirect[]>(initialRows ?? []);
  const [loaded, setLoaded] = useState((initialRows?.length ?? 0) > 0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | "new" | null>(null);
  const [status, setStatus] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [importing, setImporting] = useState(false);        // CSV import in flight → spinner beside the button
  const [failedRows, setFailedRows] = useState<string[]>([]); // rows that failed to import, listed at top
  // Pending import (after parsing, before running) → the confirm modal with new/overwrite choice.
  type ImportEntry = { source: string; destination: string };
  type SkippedRow = { reason: string; text: string };
  const [importPrompt, setImportPrompt] = useState<{ entries: ImportEntry[]; newOnes: ImportEntry[]; dups: (ImportEntry & { oldDest: string })[]; totalRows: number; skipped: SkippedRow[] } | null>(null);
  const [showDups, setShowDups] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [nw, setNw] = useState<{ source: string; destination: string; status: number }>({ source: "", destination: "", status: 301 });
  const fileRef = useRef<HTMLInputElement | null>(null);

  const client = getClient();

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try { setRows(await client.listRedirects()); setLoaded(true); }
    catch { setStatus({ tone: "err", text: "Could not load redirects." }); }
    finally { setLoading(false); }
  }, [client]);
  useEffect(() => { void load(); }, [load]);

  const setRow = (id: number, patch: Partial<LsgRedirect>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const saveRow = async (r: LsgRedirect) => {
    if (!client) return;
    if (!r.source.trim() || !r.destination.trim()) { setStatus({ tone: "err", text: "Source path and destination URL are required." }); return; }
    setBusyId(r.id);
    try {
      // Editing an imported/LSG-auto redirect in this tab marks it "…-edited" (→ "Import - Edited" /
      // "LSG - Edited" pill). Manual and already-edited rows keep their createdBy.
      const createdBy = (r.createdBy === "import" || r.createdBy === "lsg") ? `${r.createdBy}-edited` : undefined;
      const res = await client.updateRedirect(r.id, { source: r.source, destination: r.destination, status: r.status, ...(createdBy ? { createdBy } : {}) });
      if (res.success === false) throw new Error(res.error ?? "Save failed");
      setStatus({ tone: "ok", text: "Redirect saved." });
      await load();
      onRedirectsChanged?.();
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Save failed." }); }
    finally { setBusyId(null); }
  };
  // Enable/disable a redirect without deleting it. The front-end engine only performs redirects
  // WHERE enabled = 1, so toggling off truly stops it. Optimistic; reverts on failure.
  const toggleEnabled = async (r: LsgRedirect) => {
    if (!client) return;
    const next = !r.enabled;
    setRow(r.id, { enabled: next });
    setBusyId(r.id);
    try {
      const res = await client.updateRedirect(r.id, { enabled: next });
      if (res.success === false) throw new Error(res.error ?? "Update failed");
      setStatus({ tone: "ok", text: next ? "Redirect enabled." : "Redirect disabled." });
      onRedirectsChanged?.();
    } catch (e) {
      setRow(r.id, { enabled: !next }); // revert
      setStatus({ tone: "err", text: e instanceof Error ? e.message : "Update failed." });
    } finally { setBusyId(null); }
  };
  const deleteRow = async (r: LsgRedirect) => {
    if (!client) return;
    const auto = r.createdBy !== "manual" && r.createdBy !== "import"; // auto-applied after a page move
    const notice = auto
      ? "\n\n⚠ This redirect was applied automatically after a page move. Removing it means visitors and search engines hitting the old URL will get a 404."
      : "\n\n⚠ Removing this redirect may cause a 404 for anyone still linking to the old URL.";
    const ok = await showConfirm(`Delete the redirect from “${r.source}” → “${r.destination}”?${notice}`, { title: "Delete Redirect", tone: "danger", confirmLabel: "Delete" });
    if (!ok) return;
    setBusyId(r.id);
    try { await client.deleteRedirect(r.id); setStatus({ tone: "ok", text: "Redirect deleted." }); await load(); onRedirectsChanged?.(); }
    catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Delete failed." }); }
    finally { setBusyId(null); }
  };
  const addRow = async () => {
    if (!client) return;
    if (!nw.source.trim() || !nw.destination.trim()) { setStatus({ tone: "err", text: "Source path and destination URL are required." }); return; }
    setBusyId("new");
    try {
      const res = await client.createRedirect({ source: nw.source, destination: nw.destination, status: nw.status, createdBy: "manual" });
      if (res.success === false) throw new Error(res.error ?? "Add failed");
      void recordHistory({ action: "redirect_added", url: `${nw.source} → ${nw.destination}`, siteKey: siteKey(nw.destination) });
      setNw({ source: "", destination: "", status: 301 });
      setStatus({ tone: "ok", text: "Redirect added." });
      await load();
      onRedirectsChanged?.();
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Add failed." }); }
    finally { setBusyId(null); }
  };

  // Export every redirect as a CSV (BOM prefixed so Excel reads UTF-8). Values with commas/quotes/
  // newlines are RFC-4180 quoted.
  const exportCsv = () => {
    if (!rows.length) { setStatus({ tone: "err", text: "No redirects to export." }); return; }
    const esc = (v: string) => (/[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const header = ["Source path", "Destination URL", "Type", "Created By", "Enabled", "Date"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push([
        esc(r.source), esc(r.destination), String(r.status),
        r.createdBy !== "manual" ? "LSG" : "Manual",
        r.enabled ? "Enabled" : "Disabled",
        esc(r.createdAt || ""),
      ].join(","));
    }
    const blob = new Blob([`﻿${lines.join("\r\n")}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `redirects-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Parse a CSV (column 1 = Source, column 2 = Destination; a Source/Destination header is skipped),
  // de-dupe within the file by source (last wins), classify each row as NEW vs a duplicate of an
  // existing redirect, then open the confirm modal with the new/overwrite choice.
  const importCsv = async (file: File) => {
    if (!client) return;
    let text = "";
    try { text = await file.text(); } catch { setStatus({ tone: "err", text: "Could not read the file." }); return; }
    const parsed = parseCsv(text).map(r => r.map(c => c.trim())).filter(r => r.some(c => c !== ""));
    if (parsed.length && /^source/i.test(parsed[0][0] ?? "") && /^dest/i.test(parsed[0][1] ?? "")) parsed.shift(); // drop header
    const totalRows = parsed.length;
    // Build the unique valid set, tracking exactly which rows are dropped and why (so 75→67 is explained).
    const bySource = new Map<string, ImportEntry>();
    const seen = new Set<string>();
    const skipped: SkippedRow[] = [];
    for (const r of parsed) {
      const source = (r[0] ?? "").trim(), destination = (r[1] ?? "").trim();
      if (!source || !destination) { skipped.push({ reason: "missing", text: r.join(", ") || "(empty row)" }); continue; }
      const key = normSource(source);
      if (seen.has(key)) skipped.push({ reason: "dup-in-file", text: source });
      seen.add(key);
      bySource.set(key, { source, destination }); // last occurrence wins
    }
    const entries = [...bySource.values()];
    if (!entries.length) { setStatus({ tone: "err", text: "No valid rows found. Use a CSV with Source in the first column and Destination in the second." }); return; }
    const existing = new Map(rows.map(r => [normSource(r.source), r.destination]));
    const dups = entries.filter(e => existing.has(normSource(e.source))).map(e => ({ ...e, oldDest: existing.get(normSource(e.source)) ?? "" }));
    const newOnes = entries.filter(e => !existing.has(normSource(e.source)));
    setShowDups(false);
    setShowSkipped(false);
    setStatus(null);
    setFailedRows([]);
    setImportPrompt({ entries, newOnes, dups, totalRows, skipped });
  };

  // Run the actual import for the chosen set of rows (all, or new-only). Each is a 301 with
  // Created By = "Import" (existing sources are upserted server-side, keeping their original label).
  const runImport = async (entries: ImportEntry[]) => {
    setImportPrompt(null);
    if (!client || !entries.length) return;
    setImporting(true);
    setBusyId("new");
    setFailedRows([]);
    setStatus(null);
    onToast?.({ type: "info", message: `⏳ Importing ${entries.length} redirect${entries.length === 1 ? "" : "s"}…` });
    let done = 0; const fails: string[] = [];
    for (const e of entries) {
      try {
        const res = await client.createRedirect({ source: e.source, destination: e.destination, status: 301, createdBy: "import" });
        if (res.success === false) {
          // Surface the plugin's actual reason (WP REST errors put it in data.message), not just the code.
          const wpMsg = (res.data as { message?: string } | null)?.message;
          throw new Error(wpMsg || res.error || `HTTP ${res.status}`);
        }
        done++; void recordHistory({ action: "redirect_added", url: `${e.source} → ${e.destination}`, siteKey: siteKey(e.destination) });
      } catch (err) { fails.push(`${e.source} → ${e.destination}: ${err instanceof Error ? err.message : "error"}`); }
    }
    setImporting(false);
    setBusyId(null);
    setFailedRows(fails);
    await load();
    onRedirectsChanged?.();
    const summary = `Imported ${done} of ${entries.length}${fails.length ? ` · ${fails.length} failed` : ""}.`;
    setStatus({ tone: fails.length ? "err" : "ok", text: summary });
    onToast?.({ type: fails.length ? "error" : "success", message: `${fails.length ? "" : "✓ "}${summary}` });
  };

  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";

  // ── Row virtualization (windowing) ──────────────────────────────────────────
  // Same approach as the Sitemap/History tabs: a long redirect list is thousands of DOM nodes (each
  // row carries two inputs, a select and a toggle), which froze the tab while the browser built/tore
  // it down. Render only the rows in/near the viewport inside a bounded scroller; editing/saving
  // still operate on the full `rows` state, so scrolling a row out and back preserves its values.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [rowHeight, setRowHeight] = useState(45);          // measured from a real row after first paint
  const [win, setWin] = useState({ start: 0, end: 50 });   // absolute row indices currently rendered
  const winRafRef = useRef<number | null>(null);
  const OVERSCAN = 10;
  const recomputeWindow = () => {
    const el = scrollRef.current;
    if (!el) return;
    const rh = rowHeight || 45;
    const total = rows.length;
    const start = Math.max(0, Math.floor(el.scrollTop / rh) - OVERSCAN);
    const count = Math.ceil(el.clientHeight / rh) + OVERSCAN * 2;
    const end = Math.min(total, start + count);
    setWin(prev => (prev.start === start && prev.end === end ? prev : { start, end }));
  };
  const onScrollVertical = () => {
    if (winRafRef.current != null) return;
    winRafRef.current = requestAnimationFrame(() => { winRafRef.current = null; recomputeWindow(); });
  };
  useLayoutEffect(() => {
    const first = scrollRef.current?.querySelector<HTMLElement>("tbody tr[data-rmrow]");
    if (first && first.offsetHeight > 0 && Math.abs(first.offsetHeight - rowHeight) > 1) setRowHeight(first.offsetHeight);
    recomputeWindow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, rowHeight, loaded]);
  useEffect(() => {
    const onResize = () => recomputeWindow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, rowHeight]);

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div className="sh"><h2>Redirection Manager</h2><p>301/302 redirects on your connected WordPress site</p></div>

      {/* Import confirm modal — summary of new vs. duplicate sources + import choice. */}
      {importPrompt && (() => {
        const { entries, newOnes, dups, totalRows, skipped } = importPrompt;
        const missingCount = skipped.filter(s => s.reason === "missing").length;
        const dupInFileCount = skipped.filter(s => s.reason === "dup-in-file").length;
        const panelBg = isDark ? "#0f172a" : "#ffffff";
        const brd = isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid #e2e8f0";
        const textColor = isDark ? "#e2e8f0" : "#0f172a";
        const labelColor = isDark ? "rgba(255,255,255,.6)" : "#64748b";
        const subBg = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onMouseDown={() => setImportPrompt(null)}>
            <div style={{ width: 860, maxWidth: "100%", background: panelBg, border: brd, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.45)", padding: 22, fontFamily: "'Inter','Mulish',sans-serif" }} onMouseDown={e => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 800, color: textColor, marginBottom: 8 }}>Import Redirects</div>
              <div style={{ fontSize: 13, color: labelColor, lineHeight: 1.55, marginBottom: 14 }}>
                This CSV has <strong style={{ color: textColor }}>{entries.length}</strong> redirect{entries.length === 1 ? "" : "s"}: <strong style={{ color: "#16a34a" }}>{newOnes.length} new</strong>
                {dups.length > 0
                  ? <> · <strong style={{ color: "#f59e0b" }}>{dups.length}</strong> already exist and will have their <strong style={{ color: textColor }}>destination updated</strong> if you import all.</>
                  : <>. None match an existing redirect.</>}
              </div>
              {skipped.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12.5, color: labelColor, lineHeight: 1.5, marginBottom: 14 }}>
                    <strong style={{ color: textColor }}>{totalRows}</strong> data row{totalRows === 1 ? "" : "s"} in the file · <strong style={{ color: "#ef4444" }}>{skipped.length}</strong> skipped
                    {" ("}{[missingCount ? `${missingCount} missing a source or destination` : "", dupInFileCount ? `${dupInFileCount} duplicate source${dupInFileCount === 1 ? "" : "s"} within the file` : ""].filter(Boolean).join(", ")}{")"}.
                  </div>
                  <button onClick={() => setShowSkipped(v => !v)} style={{ background: "none", border: "none", color: "#2563eb", fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
                    {showSkipped ? "Hide" : "View"} {skipped.length} skipped row{skipped.length === 1 ? "" : "s"}
                  </button>
                  {showSkipped && (
                    <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", overflowX: "auto", background: subBg, border: brd, borderRadius: 8, padding: "8px 10px", fontFamily: "'Roboto Mono', monospace", fontSize: 11.5, lineHeight: 1.6, color: textColor }}>
                      {skipped.map((s, i) => (
                        <div key={i} style={{ whiteSpace: "nowrap" }}>
                          <span style={{ color: "#ef4444" }}>[{s.reason === "missing" ? "missing field" : "duplicate in file"}]</span> <span style={{ color: labelColor }}>{s.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {dups.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <button onClick={() => setShowDups(v => !v)} style={{ background: "none", border: "none", color: "#2563eb", fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
                    {showDups ? "Hide" : "View"} {dups.length} duplicate source{dups.length === 1 ? "" : "s"}
                  </button>
                  {showDups && (
                    <div style={{ marginTop: 8, maxHeight: 260, overflowY: "auto", overflowX: "auto", background: subBg, border: brd, borderRadius: 8, padding: "8px 10px", fontFamily: "'Roboto Mono', monospace", fontSize: 11.5, lineHeight: 1.7, color: "#f59e0b" }}>
                      {dups.map((d, i) => (
                        <div key={i} style={{ whiteSpace: "nowrap" }}>{d.source}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button onClick={() => setImportPrompt(null)} style={{ padding: "9px 16px", background: isDark ? "transparent" : "#fff", color: isDark ? "#e2e8f0" : "#475569", border: brd, borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>Cancel</button>
                {dups.length > 0 && (
                  <button onClick={() => void runImport(newOnes)} disabled={newOnes.length === 0} style={{ padding: "9px 16px", background: newOnes.length === 0 ? "#93c5a3" : "#2563eb", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: newOnes.length === 0 ? "not-allowed" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>Import new only ({newOnes.length})</button>
                )}
                <button onClick={() => void runImport(entries)} style={{ padding: "9px 18px", background: "linear-gradient(135deg,#15803d,#16a34a)", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>
                  {dups.length > 0 ? `Import all — update existing (${entries.length})` : `Import (${entries.length})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {!connected || !client ? (
        <div className="cd-section">
          <p className="cd-help">Connect your WordPress site (top of the page) to view and manage its redirects.</p>
        </div>
      ) : (
        <div className="cd-section">
          <p className="cd-help" style={{ marginBottom: 14 }}>
            Redirects are created automatically when you change a page&rsquo;s parent in the Quick Edit Sitemap tab, and you can add or edit them here. Edit the source path, destination URL, or type (301 permanent / 302 temporary), then Save.
          </p>
          {status && <div style={{ fontSize: 12.5, marginBottom: 12, fontWeight: 600, color: status.tone === "err" ? "#dc2626" : "#16a34a", fontFamily: "'Inter','Mulish',sans-serif" }}>{status.text}</div>}

          {/* Failed import rows — listed at the top so the user sees exactly what didn't import. */}
          {failedRows.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,.1)", border: "1px solid rgba(220,38,38,.4)", fontFamily: "'Inter','Mulish',sans-serif" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#ef4444" }}>{failedRows.length} row{failedRows.length === 1 ? "" : "s"} failed to import</span>
                <button onClick={() => setFailedRows([])} aria-label="Dismiss" style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: "auto", fontSize: 12, lineHeight: 1.6, color: "#ef4444", fontFamily: "'Roboto Mono', monospace" }}>
                {failedRows.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}

          {/* Import / Export CSV — right-aligned, above the Add Redirect button */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 30 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={e => { const f = e.target.files?.[0]; e.currentTarget.value = ""; if (f) void importCsv(f); }}
            />
            {importing && <span className="lsg-spinner" title="Importing…" style={{ verticalAlign: "middle" }} />}
            <button
              className="tip-right"
              onClick={() => fileRef.current?.click()}
              disabled={busyId === "new"}
              data-tip="Import redirects from a CSV — the first column must be Source, the second column must be Destination"
              aria-label="Import redirects from CSV"
              onMouseEnter={e => { if (busyId !== "new") { e.currentTarget.style.color = "#1d4ed8"; e.currentTarget.style.transform = "scale(1.12)"; } }}
              onMouseLeave={e => { e.currentTarget.style.color = "#2563eb"; e.currentTarget.style.transform = "scale(1)"; }}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "6px 8px", background: "none", border: "none", borderRadius: 6, lineHeight: 0, color: "#2563eb", cursor: busyId === "new" ? "default" : "pointer", opacity: busyId === "new" ? 0.5 : 1, transition: "color .15s, transform .15s" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            </button>
            <button
              className="tip-right"
              onClick={exportCsv}
              disabled={!rows.length}
              data-tip="Export all redirects as a CSV file"
              aria-label="Export redirects as CSV"
              onMouseEnter={e => { if (rows.length) { e.currentTarget.style.color = "#15803d"; e.currentTarget.style.transform = "scale(1.12)"; } }}
              onMouseLeave={e => { e.currentTarget.style.color = rows.length ? "#16a34a" : "#94a3b8"; e.currentTarget.style.transform = "scale(1)"; }}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "6px 8px", background: "none", border: "none", borderRadius: 6, lineHeight: 0, color: rows.length ? "#16a34a" : "#94a3b8", cursor: rows.length ? "pointer" : "not-allowed", opacity: rows.length ? 1 : 0.45, transition: "color .15s, transform .15s" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={rows.length ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
            </button>
          </div>

          {/* Add a new redirect */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <input value={nw.source} onChange={e => setNw({ ...nw, source: e.target.value })} placeholder="/old-path" style={{ ...fieldInput, flex: "1 1 180px" }} />
            <span style={{ color: "#94a3b8" }}>→</span>
            <input value={nw.destination} onChange={e => setNw({ ...nw, destination: e.target.value })} placeholder="https://yoursite.com/new-path/" style={{ ...fieldInput, flex: "2 1 260px" }} />
            <select value={nw.status} onChange={e => setNw({ ...nw, status: Number(e.target.value) })} style={{ ...fieldInput, width: 96, cursor: "pointer" }}>
              <option value={301}>301</option><option value={302}>302</option>
            </select>
            <button onClick={() => void addRow()} disabled={busyId === "new"} style={actBtn("#16a34a")}>{busyId === "new" ? "Adding…" : "+ Add Redirect"}</button>
          </div>

          {loading && !loaded ? (
            <p className="cd-help">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="cd-help">No redirects yet. Change a page&rsquo;s parent in the Quick Edit Sitemap tab, or add one above.</p>
          ) : (
            <div ref={scrollRef} onScroll={onScrollVertical} style={{ overflow: "auto", maxHeight: "70vh" }}>
            <table className="cd-history" style={{ margin: 0 }}>
              <thead><tr>
                <th style={{ position: "sticky", top: 0, zIndex: 2 }}>Source path</th>
                <th style={{ position: "sticky", top: 0, zIndex: 2 }}>Destination URL</th>
                <th style={{ width: 96, position: "sticky", top: 0, zIndex: 2 }}>Type</th>
                <th style={{ width: 96, position: "sticky", top: 0, zIndex: 2 }} data-tip={`"- Edited" here means the redirect was manually edited in this tab after being imported, or after Local SEO Garage added it automatically when you changed a page's URL in the Quick Edit Sitemap.`}>Created By</th>
                <th style={{ width: 120, position: "sticky", top: 0, zIndex: 2 }}>Date</th>
                <th style={{ width: 92, textAlign: "center", position: "sticky", top: 0, zIndex: 2 }}>Enable/Disable</th>
                <th style={{ width: 110, position: "sticky", top: 0, zIndex: 2 }}>Update</th>
              </tr></thead>
              <tbody>
                {win.start > 0 && (
                  <tr aria-hidden="true"><td colSpan={7} style={{ height: win.start * rowHeight, padding: 0, border: "none" }} /></tr>
                )}
                {rows.slice(win.start, win.end).map(r => (
                  <tr key={r.id} data-rmrow="">
                    <td><input value={r.source} onChange={e => setRow(r.id, { source: e.target.value })} style={fieldInput} /></td>
                    <td><input value={r.destination} onChange={e => setRow(r.id, { destination: e.target.value })} style={fieldInput} /></td>
                    <td>
                      <select value={r.status} onChange={e => setRow(r.id, { status: Number(e.target.value) })} style={{ ...fieldInput, cursor: "pointer" }}>
                        <option value={301}>301</option><option value={302}>302</option>
                      </select>
                    </td>
                    <td style={{ verticalAlign: "middle" }}>{(() => { const p = createdByPill(r.createdBy); return <span className={`rm-pill ${p.cls}`}>{p.label}</span>; })()}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569", fontFamily: "'Inter','Mulish',sans-serif", verticalAlign: "middle" }}>{fmtRedirectDate(r.createdAt)}</td>
                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={r.enabled}
                        onClick={() => void toggleEnabled(r)}
                        disabled={busyId === r.id}
                        title={r.enabled ? "Redirect enabled — click to disable (keeps it without redirecting)" : "Redirect disabled — click to enable"}
                        aria-label={r.enabled ? "Disable redirect" : "Enable redirect"}
                        style={{ position: "relative", width: 34, height: 18, borderRadius: 999, border: `1px solid ${r.enabled ? "#16a34a" : "#cbd5e1"}`, background: r.enabled ? "#16a34a" : "#e2e8f0", cursor: busyId === r.id ? "default" : "pointer", padding: 0, verticalAlign: "middle", flexShrink: 0, transition: "background .15s, border-color .15s", opacity: busyId === r.id ? 0.6 : 1 }}
                      >
                        <span style={{ position: "absolute", top: 1, left: r.enabled ? 17 : 1, width: 14, height: 14, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s" }} />
                      </button>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button onClick={() => void saveRow(r)} disabled={busyId === r.id} style={{ ...actBtn("#2563eb"), borderRadius: 0, marginRight: 8 }}>Save</button>
                      <button
                        onClick={() => void deleteRow(r)}
                        disabled={busyId === r.id}
                        title="Delete redirect"
                        aria-label="Delete redirect"
                        style={{ background: "none", border: "none", cursor: busyId === r.id ? "default" : "pointer", color: "#dc2626", padding: 4, lineHeight: 0, verticalAlign: "middle", opacity: busyId === r.id ? 0.5 : 1 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                      </button>
                    </td>
                  </tr>
                ))}
                {win.end < rows.length && (
                  <tr aria-hidden="true"><td colSpan={7} style={{ height: (rows.length - win.end) * rowHeight, padding: 0, border: "none" }} /></tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
